// N38 · Phase 1.1 — 中文时间表达式 → slot 区间（纯函数，无副作用，无 Date.now()）
//
// 输入：一句话（可能含活动词，如 "早上9点到10点吃早饭"），扫描其中的时间表达式。
// 输出：{ ok, startSlot, endSlot, startMin, endMin, crossMidnight, kind, matched }
//   - startSlot / endSlot：slot 索引（含两端，[startSlot, endSlot]），落库时写这段
//   - startMin / endMin：当日分钟（0–1439）；跨午夜时 endMin 可 > 1440
//   - crossMidnight：true 表示结束跨过午夜（落库到次日，交 Phase 1.3 处理）
//   - kind：'range' | 'point' | 'relative'
// 相对时间（"刚才半小时"）需要传 nowMin（当日分钟），不传则该类不识别。
//
// 设计：只覆盖结构化高频说法（"X点到Y点""上午X点""刚才半小时"），覆盖不到的交「复述确认」兜底。

import { SLOTS } from '../data/slots.js';

const SLOT_BOUNDS = SLOTS.map(s => {
  const start = s.h * 60 + s.m;
  return { start, end: start + (s.span === 0.5 ? 30 : 60) };
});

// 当日分钟 → 包含它的 slot 索引（startMin ≤ min < endMin）
function slotContaining(min) {
  const m = ((min % 1440) + 1440) % 1440;
  for (let i = 0; i < SLOT_BOUNDS.length; i++) {
    if (m >= SLOT_BOUNDS[i].start && m < SLOT_BOUNDS[i].end) return i;
  }
  return -1;
}

const CN = { 零: 0, 一: 1, 二: 2, 两: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9, 十: 10 };
// 中文/阿拉伯数字 → int（支持 十/十X/X十/X十X，范围足够覆盖小时 0–23、分钟 0–59）
function cnNum(s) {
  if (s == null) return null;
  s = String(s).trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  if (!/^[零一二两三四五六七八九十]+$/.test(s)) return null;
  if (s === '十') return 10;
  if (s.length === 1) return CN[s] ?? null;
  if (s[0] === '十') return 10 + (CN[s[1]] ?? 0);          // 十一..十九
  const idx = s.indexOf('十');
  if (idx > 0) {                                            // 二十 / 二十三
    const tens = (CN[s[0]] ?? 0) * 10;
    const ones = idx < s.length - 1 ? (CN[s[idx + 1]] ?? 0) : 0;
    return tens + ones;
  }
  return CN[s] ?? null;
}

// "半"/"一刻"/"三刻"/"30"/"30分" → 分钟数
function parseHalf(str) {
  if (!str) return 0;
  if (str.includes('半')) return 30;
  if (str.includes('一刻')) return 15;
  if (str.includes('三刻')) return 45;
  const m = str.match(/(\d{1,2}|[零一二两三四五六七八九十]+)/);
  if (m) { const n = cnNum(m[1]); return n == null ? 0 : n; }
  return 0;
}

// 把 12 小时制 + 时段词 调成 24 小时制
function periodAdjust(h, period) {
  if (!period) return h;
  if (/凌晨|半夜/.test(period)) return h === 12 ? 0 : h;        // 凌晨1=1, 半夜12=0
  if (/早上|早晨|清晨|上午/.test(period)) return h;             // 早上6=6, 上午9=9
  if (/中午/.test(period)) return h <= 1 ? h + 12 : h;          // 中午12=12, 中午1=13
  if (/下午|傍晚/.test(period)) return h < 12 ? h + 12 : h;     // 下午3=15, 下午12=12
  if (/晚上|夜里|夜间|今晚/.test(period)) {                     // 晚上8=20, 晚上11=23, 晚上12=0
    if (h === 12) return 0;
    return h < 12 ? h + 12 : h;
  }
  return h;
}

const PERIOD = '凌晨|早上|早晨|清晨|上午|中午|下午|傍晚|晚上|夜里|夜间|今晚|半夜';
const NUM = '\\d{1,2}|[零一二两三四五六七八九十]+';
const HALF = '半|一刻|三刻|\\d{1,2}\\s*分?|[零一二两三四五六七八九十]+\\s*分';
// 一个"钟点"：可带时段前缀；分隔符 点/时/:/：；可带 半/分
const CLOCK_G = new RegExp(`(${PERIOD})?\\s*(${NUM})\\s*[点时時:：]\\s*(${HALF})?`, 'g');

// 在句子里找出所有钟点，返回 [{ hour, minutePart, period, index, length }]
function findClocks(text) {
  const out = [];
  let m;
  CLOCK_G.lastIndex = 0;
  while ((m = CLOCK_G.exec(text)) !== null) {
    const hour = cnNum(m[2]);
    if (hour == null || hour > 24) continue;
    out.push({
      hour,
      minutePart: parseHalf(m[3]),
      period: m[1] || null,
      index: m.index,
      length: m[0].length,
    });
  }
  return out;
}

const REL_G = new RegExp(`(刚才|刚刚|过去|这|近)?\\s*(${NUM}|半)?\\s*个?\\s*(半小时|小时|钟头|分钟|分)`, '');

function clockToMin(c, inheritPeriod) {
  const period = c.period || inheritPeriod || null;
  const h = periodAdjust(c.hour, period);
  return h * 60 + c.minutePart;
}

function rangeConnector(gap) {
  // 两个钟点之间只允许 空白 + 到/至/-/~/—/～ 这类连接符
  return /^[\s到至\-~—－～]*$/.test(gap) && /[到至\-~—－～]/.test(gap);
}

// 「刚才」+ 裸钟点（无时段词、无 hint）→ 认成最近一次出现的该钟点：
// 18:10 说"刚才6点" = 18:00（下午档 ≤ now 就用下午档）；15:00 说"刚才6点" = 早上 6:00。
function adjustRecent(baseMin, clock, text, hint, nowMin) {
  if (clock.period || hint || nowMin == null) return baseMin;
  if (!/刚才|刚刚/.test(text)) return baseMin;
  if (clock.hour >= 12) return baseMin;
  const alt = baseMin + 720;
  return alt <= nowMin ? alt : baseMin;
}

function pack(startMin, endMin, kind, matched) {
  let cross = false;
  let endForSlot = endMin;
  if (endMin <= startMin) { cross = true; endForSlot = endMin + 1440; }
  const startSlot = slotContaining(startMin);
  const endSlot = slotContaining((endForSlot - 1) % 1440 < 0 ? 0 : (endForSlot - 1)); // 最后一格 = 结束时刻所在格的前一格
  return {
    ok: startSlot >= 0 && endSlot >= 0,
    startSlot, endSlot,
    startMin, endMin: cross ? endMin + 1440 : endMin,
    crossMidnight: cross,
    kind, matched,
  };
}

// periodHint：调用方从语境推出的时段词（如餐别"晚饭"→'晚上'），只对没带显式时段词的钟点生效。
// 优先级：钟点自带时段词 > periodHint > 「刚才」就近 > 原值。
export function parseTimeRange(text, { nowMin = null, periodHint = null } = {}) {
  if (!text) return { ok: false };
  const t = String(text);

  // 1) 相对时间："刚才半小时""过去一个小时""刚才30分钟"
  if (nowMin != null) {
    const rm = t.match(REL_G);
    if (rm && /刚才|刚刚|过去|这|近/.test(rm[1] || '')) {
      let dur;
      if (rm[3] === '半小时') dur = 30;
      else if (rm[2] === '半') dur = (rm[3] === '小时' || rm[3] === '钟头') ? 30 : 30;
      else {
        const n = rm[2] ? cnNum(rm[2]) : 1;
        dur = (rm[3] === '小时' || rm[3] === '钟头') ? n * 60 : n;
      }
      const endMin = nowMin;
      const startMin = nowMin - dur;
      if (startMin >= 0) return pack(startMin, endMin, 'relative', rm[0]);
    }
  }

  // 2) 钟点（区间 / 单点）
  const clocks = findClocks(t);
  if (clocks.length >= 2) {
    const a = clocks[0], b = clocks[1];
    const gap = t.slice(a.index + a.length, b.index);
    if (rangeConnector(gap)) {
      const baseStart = clockToMin(a, periodHint);
      const startMin = adjustRecent(baseStart, a, t, periodHint, nowMin);
      // 右端无时段则继承左端（"下午3点到5点"）；左端被「刚才」就近平移过的话右端跟着平移
      const endMin = clockToMin(b, a.period || periodHint) + (b.period ? 0 : startMin - baseStart);
      return pack(startMin, endMin, 'range', t.slice(a.index, b.index + b.length));
    }
  }
  if (clocks.length >= 1) {
    const a = clocks[0];
    const baseStart = clockToMin(a, periodHint);
    const startMin = adjustRecent(baseStart, a, t, periodHint, nowMin);
    // 单点＝只占所在的那一个格（"19点14分拉屎"这类点事件按最短算，不是 +1 小时；
    // 半小时格区域即 30 分钟格。2026-06-06 用户拍板）
    const pos = ((startMin % 1440) + 1440) % 1440;
    const si = slotContaining(pos);
    const endMin = si >= 0 ? startMin + (SLOT_BOUNDS[si].end - pos) : startMin + 60;
    return pack(startMin, endMin, 'point', t.slice(a.index, a.index + a.length));
  }

  return { ok: false };
}

// 给外部直接造一个区间结果（checkinPlan 餐别词无钟点时的"倒推 1 小时"兜底用）
export function makeRange(startMin, endMin, kind = 'relative', matched = '') {
  return pack(startMin, endMin, kind, matched);
}

// 调试用：slot 索引 → "HH:MM" 标签
export function slotLabel(i) { return SLOTS[i] ? SLOTS[i].label : `#${i}`; }
